import { useCallback, useEffect, useMemo, useState } from 'react';
import { MessageSquare, Search } from 'lucide-react';
import {
  fetchAdminConversations,
  type AdminConversationSummary,
  type AdminMessageUserRole,
} from '@/lib/messageService';
import {
  Badge,
  Button,
  DataTable,
  Loading,
  Pagination,
  ResizableDetailLayout,
  type Column,
} from '@/components/ui';
import { MessageDetailPanel } from './MessageDetailPage';

const PAGE_SIZE = 20;

const roleLabels: Record<AdminMessageUserRole, string> = {
  donor: 'ドナー',
  donee: 'ドニー',
  unknown: '不明',
};

const roleVariants: Record<AdminMessageUserRole, 'default' | 'info' | 'success'> = {
  donor: 'info',
  donee: 'success',
  unknown: 'default',
};

function formatDateTime(date?: Date): string {
  if (!date) {
    return '-';
  }

  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function participantSearchText(conversation: AdminConversationSummary): string {
  return conversation.participantSummaries
    .flatMap((participant) => [
      participant.displayName,
      participant.email,
      participant.role,
      participant.status ?? '',
    ])
    .join(' ')
    .toLowerCase();
}

export function MessagesPage() {
  const [conversations, setConversations] = useState<AdminConversationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);

  const loadConversations = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const nextConversations = await fetchAdminConversations();
      setConversations(nextConversations);
    } catch (loadError) {
      console.error('Failed to fetch conversations:', loadError);
      setError('メッセージ一覧の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConversations();
  }, [loadConversations]);

  const filteredConversations = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return conversations;
    }

    return conversations.filter((conversation) => {
      const haystack = [
        participantSearchText(conversation),
        conversation.lastMessage ?? '',
        conversation.id,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [conversations, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredConversations.length / PAGE_SIZE));
  const pagedConversations = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredConversations.slice(startIndex, startIndex + PAGE_SIZE);
  }, [currentPage, filteredConversations]);
  const visibleStart =
    filteredConversations.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const visibleEnd = Math.min(currentPage * PAGE_SIZE, filteredConversations.length);

  const columns: Column<AdminConversationSummary>[] = [
    {
      key: 'participants',
      header: '参加者',
      render: (conversation) => (
        <div className="space-y-2">
          {conversation.participantSummaries.map((participant) => (
            <div key={participant.id}>
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-semibold text-admin-ink">{participant.displayName}</p>
                <Badge variant={roleVariants[participant.role]} size="sm">
                  {roleLabels[participant.role]}
                </Badge>
              </div>
              <p className="text-xs text-admin-muted">{participant.email || participant.id}</p>
            </div>
          ))}
        </div>
      ),
    },
    {
      key: 'lastMessage',
      header: '最終メッセージ',
      render: (conversation) => (
        <div className="min-w-[260px] max-w-[460px]">
          <p className="line-clamp-2 text-sm text-admin-ink">
            {conversation.lastMessage ?? 'メッセージなし'}
          </p>
          <p className="mt-1 text-xs text-admin-muted">
            {formatDateTime(conversation.lastMessageAt ?? conversation.updatedAt)}
          </p>
        </div>
      ),
    },
    {
      key: 'unreadTotal',
      header: '未読',
      render: (conversation) =>
        conversation.unreadTotal > 0 ? (
          <Badge variant="warning" size="sm">
            {conversation.unreadTotal}
          </Badge>
        ) : (
          <span className="text-xs text-admin-muted">-</span>
        ),
    },
    {
      key: 'updatedAt',
      header: '更新日時',
      render: (conversation) => (
        <span className="text-xs text-admin-muted">
          {formatDateTime(conversation.updatedAt ?? conversation.lastMessageAt)}
        </span>
      ),
    },
  ];

  if (isLoading) {
    return <Loading fullScreen message="メッセージ一覧を読み込み中..." />;
  }

  return (
    <ResizableDetailLayout
      isOpen={Boolean(selectedConversationId)}
      detail={
        selectedConversationId ? (
          <MessageDetailPanel
            conversationId={selectedConversationId}
            embedded
            onClose={() => setSelectedConversationId(null)}
          />
        ) : null
      }
      list={
        <section className="space-y-5 p-5 md:p-8">
          <header className="flex flex-wrap items-end justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-admin-accent/10 p-3">
                <MessageSquare className="h-6 w-6 text-admin-accent" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-admin-ink">メッセージ管理</h1>
                <p className="text-sm text-admin-muted">ドナーとドニー間の会話を確認します。</p>
              </div>
            </div>
            <Button
              type="button"
              onClick={() => void loadConversations()}
              disabled={isLoading}
              variant="secondary"
            >
              再読み込み
            </Button>
          </header>

          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="rounded-2xl border border-admin-border bg-white p-4 shadow-sm">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-admin-muted" />
              <input
                type="search"
                value={searchQuery}
                placeholder="表示名・メール・メッセージで検索"
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                  setCurrentPage(1);
                }}
                className="w-full rounded-xl border border-admin-border py-2 pl-9 pr-3 text-sm focus:border-admin-accent focus:outline-none focus:ring-2 focus:ring-admin-accent/20"
              />
            </div>
          </div>

          <div className="text-xs text-admin-muted">
            表示件数: {visibleStart}-{visibleEnd} / {filteredConversations.length}
            {filteredConversations.length !== conversations.length ? `（全${conversations.length}件）` : ''}
          </div>

          <DataTable
            data={pagedConversations}
            columns={columns}
            onRowClick={(conversation) => setSelectedConversationId(conversation.id)}
            emptyMessage="会話はまだありません"
          />

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </section>
      }
    />
  );
}
